import React, { useState, useEffect } from 'react';
import { Star, Send, Loader2, CheckCircle, X } from 'lucide-react';

// Fallback proizvodi u slučaju da API ne radi
const FALLBACK_PRODUCTS = [
  { id: "gpp-50g", name: "Ganetov Premium Puter od Kikirikija", size: "50g" },
  { id: "gpp-350g", name: "Ganetov Premium Puter od Kikirikija", size: "350g" },
  { id: "gpp-700g", name: "Ganetov Premium Puter od Kikirikija", size: "700g" },
  { id: "gpp-2kg", name: "Ganetov Premium Puter od Kikirikija", size: "2kg" }
];

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    product_id: '',
    customer_name: '',
    customer_email: '',
    rating: 5,
    comment: ''
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    if (showForm && products.length === 0) {
      fetchProducts();
    }
  }, [showForm]);

  const fetchReviews = async () => {
    try {
      const res = await fetch('/api/reviews.php');
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      const res = await fetch('/api/products.php');
      if (res.ok) {
        const data = await res.json();
        const fetchedProducts = Array.isArray(data) ? data : [];
        // Ako API vrati praznu listu, koristi fallback
        setProducts(fetchedProducts.length > 0 ? fetchedProducts : FALLBACK_PRODUCTS);
      } else {
        // Ako API nije dostupan, koristi fallback
        console.warn('Products API returned error, using fallback');
        setProducts(FALLBACK_PRODUCTS);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      // Koristi fallback proizvode ako API ne radi
      setProducts(FALLBACK_PRODUCTS);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleOpenForm = () => {
    setShowForm(true);
    if (products.length === 0) {
      fetchProducts();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    setErrorMessage('');

    try {
      const res = await fetch('/api/reviews.php?action=submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setSubmitStatus('success');
        setFormData({ product_id: '', customer_name: '', customer_email: '', rating: 5, comment: '' });
        setTimeout(() => {
          setShowForm(false);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.error || 'Došlo je do greške');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Došlo je do greške. Pokušajte ponovo.');
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => (
    <div className="stars-container">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`star-btn ${star <= rating ? 'filled' : ''} ${interactive ? 'interactive' : ''}`}
          onClick={() => interactive && onSelect && onSelect(star)}
          disabled={!interactive}
        >
          <Star size={interactive ? 28 : 16} fill={star <= rating ? '#d4a853' : 'none'} />
        </button>
      ))}
    </div>
  );

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? `${product.name} (${product.size})` : productId;
  };

  // Format name: "Marko Petrović" -> "Marko P."
  const formatName = (fullName) => {
    const parts = fullName.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
    }
    return fullName;
  };

  return (
    <section id="reviews" className="reviews-section">
      <div className="section-container">
        <span className="section-label">RECENZIJE</span>
        <h2 className="reviews-title">ŠTA KAŽU NAŠI KUPCI</h2>
        
        {loading ? (
          <div className="reviews-loading">
            <Loader2 className="loading-spinner" size={32} />
          </div>
        ) : reviews.length > 0 ? (
          <div className="reviews-grid">
            {reviews.slice(0, 6).map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="review-author">
                    <div className="author-avatar">{review.customer_name.charAt(0).toUpperCase()}</div>
                    <div className="author-info">
                      <span className="author-name">{formatName(review.customer_name)}</span>
                    </div>
                  </div>
                  {renderStars(review.rating)}
                </div>
                {review.comment && <p className="review-comment">"{review.comment}"</p>}
              </div>
            ))}
          </div>
        ) : (
          <p className="no-reviews">Budite prvi koji će ostaviti recenziju!</p>
        )}

        <button className="add-review-btn" onClick={handleOpenForm}>
          <Star size={18} />
          Ostavite recenziju
        </button>

        {showForm && (
          <div className="review-modal-overlay" onClick={() => setShowForm(false)}>
            <div className="review-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowForm(false)}><X size={24} /></button>

              {submitStatus === 'success' ? (
                <div className="review-success">
                  <CheckCircle size={48} />
                  <h3>Hvala na recenziji!</h3>
                  <p>Vaša recenzija će biti objavljena nakon pregleda.</p>
                </div>
              ) : (
                <>
                  <h3 className="modal-title">Ostavite recenziju</h3>
                  <p className="modal-subtitle">Podelite svoje iskustvo sa našim proizvodima</p>

                  <form onSubmit={handleSubmit} className="review-form">
                    <div className="form-group">
                      <label>Izaberite proizvod *</label>
                      {productsLoading ? (
                        <div style={{ padding: '12px', color: '#888' }}>
                          <Loader2 size={16} className="loading-spinner" style={{ display: 'inline', marginRight: '8px' }} />
                          Učitavanje proizvoda...
                        </div>
                      ) : (
                        <select
                          value={formData.product_id}
                          onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                          required
                        >
                          <option value="">-- Izaberite proizvod --</option>
                          {products.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name} ({product.size})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Vaša ocena *</label>
                      <div className="rating-selector">
                        {renderStars(formData.rating, true, (rating) => setFormData({ ...formData, rating }))}
                        <span className="rating-text">{formData.rating}/5</span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Vaše ime *</label>
                      <input
                        type="text"
                        value={formData.customer_name}
                        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                        placeholder="Ime i prezime"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                        placeholder="vas@email.com"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Vaš komentar</label>
                      <textarea
                        value={formData.comment}
                        onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                        placeholder="Opišite svoje iskustvo sa proizvodom..."
                        rows={4}
                      />
                    </div>

                    {submitStatus === 'error' && <p className="form-error">{errorMessage}</p>}

                    <button type="submit" className="submit-review-btn" disabled={submitStatus === 'loading'}>
                      {submitStatus === 'loading' ? (
                        <><Loader2 size={18} className="loading-spinner" /> Slanje...</>
                      ) : (
                        <><Send size={18} /> Pošalji recenziju</>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;
